import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { CellContext } from '@tanstack/react-table';

import { useNavigationContext } from 'contexts/SchemableFormNavigationContext/SchemableFormNavigationContext';
import { WebEDIError } from 'services/webediErrors/types';
import { cn } from 'support/helpers/cn/cn';
import { EMPTY_FIELD } from 'support/helpers/const/const';

import { CellProps } from '../../../types';
import { useCellClassNames } from '../../cellHelpers';
import { ViewWrapperCell } from '../ViewWrapperCell';

export type Props<TData, TValue> = CellContext<TData, TValue> &
  Pick<CellProps, 'fieldId' | 'isHighlighted' | 'diffStatus' | 'originalValue'>;
export const ViewBooleanCell = <TData = unknown, TValue = string>({
  row,
  cell,
  column,
  table,
  isHighlighted,
  fieldId,
  diffStatus,
  originalValue,
}: Props<TData, TValue>) => {
  const { t } = useTranslation();
  const { service } = useNavigationContext<WebEDIError>();
  const error = service.useItemByFieldPath(fieldId);
  const value = useWatch({ name: fieldId });

  const {
    columnDef: { meta: columnMeta },
  } = column;

  const cellClassNames = useCellClassNames({
    row,
    table,
    isFirstColumn: column.getIsFirstColumn(),
    isLastColumn: column.getIsLastColumn(),
  });

  return (
    <ViewWrapperCell
      fieldId={fieldId}
      isHighlighted={isHighlighted}
      className={cellClassNames}
      meta={columnMeta}
      cell={cell}
      diffStatus={diffStatus}
      originalValue={originalValue}
      formattedOriginalValue={
        typeof originalValue === 'boolean' ? (originalValue ? t('common:yes') : t('common:no')) : undefined
      }
      textAlign={columnMeta?.optionalConfig?.textAlign}
    >
      <span className="flex w-full min-w-0 items-center">
        <div
          className={cn('flex w-full min-w-0 gap-x-0.5 overflow-hidden', {
            'justify-start': columnMeta?.optionalConfig?.textAlign === 'left',
          })}
        >
          {typeof value === 'boolean' ? (
            <span className="flex-initial">{value ? t('common:yes') : t('common:no')}</span>
          ) : (
            <span className={cn({ 'text-gray-400': !error })}>{EMPTY_FIELD}</span>
          )}
        </div>
      </span>
    </ViewWrapperCell>
  );
};
